import type { TFunction } from 'i18next';
import { storageSettingsSectionPath } from './storageService';

/** API error codes returned by the backend storage layer. */
export type CloudErrorCode =
  | 'storage_auth_expired'
  | 'storage_auth_invalid'
  | 'storage_link_required'
  | 'storage_not_configured'
  | 'storage_migration_in_progress'
  | 'storage_provider_switch_requires_migration'
  | 'storage_provider_mismatch'
  | 'storage_provider_disabled'
  | 'storage_quota_exceeded'
  | 'migration_insufficient_space'
  | 'cassette_cloud_not_included'
  | 'share_binary_inactive'
  | 'google_oauth_expired'
  | 'google_oauth_invalid'
  | 'drive_link_required'
  | string;

export type BinaryInactiveReason =
  | 'provider_mismatch'
  | 'owner_trial_cloud'
  | 'owner_auth_invalid'
  | 'collaborator_unlinked'
  | 'collaborator_auth_invalid'
  | 'acl_failed'
  | string;

export type CloudErrorAction =
  | 'reconnect_storage'
  | 'link_storage'
  | 'upgrade'
  | 'upgrade_or_link'
  | 'transfer'
  | 'sync_sharing'
  | 'wait_migration'
  | 'reduce_usage'
  | 'inform_owner_reconnect'
  | 'inform_collaborator_reconnect'
  | 'retry'
  | 'generic';

export interface CloudErrorPayload {
  code: CloudErrorCode;
  status?: number;
  inactiveReason?: BinaryInactiveReason;
  requiredStorageProvider?: string;
  projectStorageProvider?: string;
  relinkRole?: 'owner' | 'collaborator';
  reason?: string;
}

export class CloudAccessError extends Error {
  readonly payload: CloudErrorPayload;

  constructor(payload: CloudErrorPayload) {
    super(payload.code);
    this.name = 'CloudAccessError';
    this.payload = payload;
  }
}

const STORAGE_AUTH_CODES = new Set<string>([
  'google_oauth_expired',
  'google_oauth_invalid',
  'storage_auth_expired',
  'storage_auth_invalid',
]);

const HANDLED_CLOUD_CODES = new Set<string>([
  ...STORAGE_AUTH_CODES,
  'storage_link_required',
  'drive_link_required',
  'storage_not_configured',
  'storage_migration_in_progress',
  'storage_provider_switch_requires_migration',
  'storage_provider_mismatch',
  'storage_provider_disabled',
  'storage_quota_exceeded',
  'migration_insufficient_space',
  'cassette_cloud_not_included',
  'share_binary_inactive',
]);

export function extractApiErrorCode(data: Record<string, unknown> | undefined | null): string {
  if (!data) return '';
  return (
    (typeof data.code === 'string' && data.code) ||
    (typeof data.errorCode === 'string' && data.errorCode) ||
    (typeof data.errorMessage === 'string' && data.errorMessage) ||
    (typeof data.ErrorMessage === 'string' && data.ErrorMessage) ||
    (typeof data.error === 'string' && data.error) ||
    ''
  );
}

export function parseCloudErrorPayload(
  data: Record<string, unknown> | undefined | null,
  status?: number,
): CloudErrorPayload | null {
  const code = extractApiErrorCode(data);
  if (!code) return null;
  const relink = data?.relinkRole;
  return {
    code,
    status,
    inactiveReason: typeof data?.inactiveReason === 'string' ? data.inactiveReason : undefined,
    requiredStorageProvider:
      typeof data?.requiredStorageProvider === 'string' ? data.requiredStorageProvider : undefined,
    projectStorageProvider:
      typeof data?.projectStorageProvider === 'string' ? data.projectStorageProvider : undefined,
    relinkRole: relink === 'owner' || relink === 'collaborator' ? relink : undefined,
    reason: typeof data?.reason === 'string' ? data.reason : undefined,
  };
}

export function isStorageAuthCode(code: string): boolean {
  return STORAGE_AUTH_CODES.has(code);
}

export function isHandledCloudErrorCode(code: string): boolean {
  return HANDLED_CLOUD_CODES.has(code);
}

function binaryInactiveAction(payload: CloudErrorPayload): CloudErrorAction {
  const reason = payload.inactiveReason;
  if (reason === 'owner_auth_invalid' || reason === 'owner_trial_cloud') {
    return payload.relinkRole === 'collaborator' ? 'inform_owner_reconnect' : 'reconnect_storage';
  }
  if (reason === 'collaborator_auth_invalid' || reason === 'collaborator_unlinked') {
    return payload.relinkRole === 'owner' ? 'inform_collaborator_reconnect' : 'link_storage';
  }
  if (reason === 'acl_failed') {
    return 'sync_sharing';
  }
  if (reason === 'provider_mismatch') {
    return 'link_storage';
  }
  return 'link_storage';
}

export function cloudErrorAction(payload: CloudErrorPayload): CloudErrorAction {
  switch (payload.code) {
    case 'storage_auth_expired':
    case 'storage_auth_invalid':
    case 'google_oauth_expired':
    case 'google_oauth_invalid':
      return 'reconnect_storage';
    case 'storage_link_required':
    case 'drive_link_required':
    case 'storage_not_configured':
      return 'link_storage';
    case 'share_binary_inactive':
      return binaryInactiveAction(payload);
    case 'storage_migration_in_progress':
      return 'wait_migration';
    case 'cassette_cloud_not_included':
      return 'upgrade_or_link';
    case 'storage_provider_switch_requires_migration':
      return 'transfer';
    case 'storage_quota_exceeded':
    case 'migration_insufficient_space':
      return 'reduce_usage';
    case 'storage_provider_disabled':
      return 'generic';
    default:
      return 'generic';
  }
}

export function providerLabelForCloudError(
  provider?: string,
  t?: TFunction,
): string {
  if (!provider) {
    return t ? t('sharing.binaryUnavailable.yourCloud', 'your cloud') : 'your cloud';
  }
  return t ? t(`storage.providers.${provider}`, provider) : provider;
}

export function cloudErrorToastMessage(payload: CloudErrorPayload, t: TFunction): string {
  const provider = providerLabelForCloudError(
    payload.requiredStorageProvider || payload.projectStorageProvider,
    t,
  );
  const action = cloudErrorAction(payload);

  switch (action) {
    case 'reconnect_storage':
      return payload.code === 'storage_auth_invalid'
        ? t('cloudErrors.reconnectInvalid', {
            provider,
            defaultValue: 'Your {{provider}} connection is invalid. Reconnect the same account in Settings.',
          })
        : t('cloudErrors.reconnectExpired', {
            provider,
            defaultValue: 'Your {{provider}} connection expired. Reconnect in Settings to continue.',
          });
    case 'link_storage':
      return t('cloudErrors.linkStorage', {
        provider,
        defaultValue: 'Link {{provider}} in Settings to access project files.',
      });
    case 'upgrade_or_link':
      return t('cloudErrors.upgradeOrLinkCassetteCloud', {
        defaultValue:
          'Cassette Cloud project storage is not included in your plan. Link your personal cloud in Settings or upgrade to Artist/Studio + Cloud.',
      });
    case 'upgrade':
      return t('cloudErrors.upgradeForCassetteCloud', {
        defaultValue: 'Cassette Cloud project storage requires Artist/Studio + Cloud. Upgrade to continue.',
      });
    case 'transfer':
      return t('cloudErrors.transferRequired', {
        provider,
        defaultValue: 'Transfer project storage to {{provider}} before switching clouds.',
      });
    case 'sync_sharing':
      return t('cloudErrors.syncSharing', {
        defaultValue: 'File sharing could not be set up. Open Collaborators and tap Sync sharing, or reconnect storage.',
      });
    case 'wait_migration':
      return t('cloudErrors.migrationInProgress', {
        defaultValue: 'Storage migration is running. Uploads and some actions are paused until it finishes.',
      });
    case 'reduce_usage':
      return t('cloudErrors.quotaExceeded', {
        defaultValue: 'Cloud storage quota exceeded. Free space or upgrade your plan.',
      });
    case 'inform_owner_reconnect':
      return t('cloudErrors.ownerMustReconnect', {
        provider,
        defaultValue: 'The track owner must reconnect {{provider}} before files are available.',
      });
    case 'inform_collaborator_reconnect':
      return t('cloudErrors.collaboratorMustReconnect', {
        provider,
        defaultValue: 'This collaborator must reconnect {{provider}} in Settings.',
      });
    case 'retry':
      return t('cloudErrors.retry', { defaultValue: 'Cloud action failed. Try again in a moment.' });
    default:
      return t('cloudErrors.generic', { defaultValue: 'Something went wrong with cloud storage. Check Settings.' });
  }
}

export function cloudErrorCtaPath(action: CloudErrorAction, provider?: string): string | null {
  switch (action) {
    case 'reconnect_storage':
      const params = new URLSearchParams();
      params.set('reason', 'expired');
      if (provider && provider !== 'none') params.set('provider', provider);
      return `/auth/storage?${params.toString()}`;
    case 'link_storage':
      return storageSettingsSectionPath();
    case 'upgrade_or_link':
      return storageSettingsSectionPath();
    case 'upgrade':
      return '/upgrade';
    case 'transfer':
      return '/settings/transfer';
    case 'sync_sharing':
      return null;
    case 'wait_migration':
      return '/settings/transfer?resume=1';
    default:
      return storageSettingsSectionPath();
  }
}

export function binaryInactiveBodyKey(reason?: string): string {
  switch (reason) {
    case 'owner_trial_cloud':
      return 'sharing.binaryUnavailable.body.ownerTrialCloud';
    case 'owner_auth_invalid':
      return 'sharing.binaryUnavailable.body.ownerAuthInvalid';
    case 'collaborator_unlinked':
      return 'sharing.binaryUnavailable.body.collaboratorUnlinked';
    case 'collaborator_auth_invalid':
      return 'sharing.binaryUnavailable.body.collaboratorAuthInvalid';
    case 'acl_failed':
      return 'sharing.binaryUnavailable.body.aclFailed';
    default:
      return 'sharing.binaryUnavailable.body.providerMismatch';
  }
}

export function dispatchCloudError(payload: CloudErrorPayload, action: CloudErrorAction): void {
  window.dispatchEvent(
    new CustomEvent('cloud-error', {
      detail: { payload, action },
    }),
  );
}

export function handleCloudApiError(status: number, data?: Record<string, unknown>): boolean {
  const payload = parseCloudErrorPayload(data, status);
  if (!payload || !isHandledCloudErrorCode(payload.code)) {
    return false;
  }

  const action = cloudErrorAction(payload);
  dispatchCloudError(payload, action);

  if (action === 'reconnect_storage' || isStorageAuthCode(payload.code)) {
    window.dispatchEvent(
      new CustomEvent('storage-auth-expired', {
        detail: {
          code: payload.code,
          provider: payload.requiredStorageProvider || payload.projectStorageProvider,
        },
      }),
    );
  }

  return true;
}
